import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { StorageQuotaExceededError } from './errors/offline.errors';

export interface StorageEstimateSnapshot {
  /** Bytes currently used by this origin, or null where unsupported. */
  usage: number | null;
  /** Bytes this origin may use, or null where unsupported. */
  quota: number | null;
  /** usage/quota in 0..1, or null when either side is unknown. */
  ratio: number | null;
  /** True once the browser has promised not to evict this origin's data. */
  persisted: boolean;
  measuredAt: number;
}

/**
 * Watches how much room the local database has left.
 *
 * This matters more than it looks. The target is 100-500 MB of structured data,
 * and a browser's quota is neither fixed nor guaranteed: Chrome grants a share
 * of free disk, shrinks it as the disk fills, and will evict a non-persistent
 * origin under pressure. So the code must never assume a size it can rely on -
 * it has to measure, warn early, and fail in a way the user can act on.
 *
 * `navigator.storage.estimate()` is a hint, not a ledger: it is padded for
 * privacy and lags recent writes. It is used for thresholds and display, never
 * as a precondition for a write.
 */
@Injectable({ providedIn: 'root' })
export class StorageService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly browser = isPlatformBrowser(this.platformId);

  /** Warn the user at this fraction of quota. */
  static readonly WARN_RATIO = 0.8;
  /** Refuse non-essential writes (prefetch, cache top-up) at this fraction. */
  static readonly CRITICAL_RATIO = 0.92;

  private readonly snapshot = signal<StorageEstimateSnapshot>({
    usage: null,
    quota: null,
    ratio: null,
    persisted: false,
    measuredAt: 0,
  });

  readonly estimate = this.snapshot.asReadonly();

  readonly isWarning = computed(() => {
    const r = this.snapshot().ratio;
    return r !== null && r >= StorageService.WARN_RATIO;
  });

  /**
   * At this point the store is nearly full. Existing work is still safe to
   * save; only optional growth should stop.
   */
  readonly isCritical = computed(() => {
    const r = this.snapshot().ratio;
    return r !== null && r >= StorageService.CRITICAL_RATIO;
  });

  readonly supportsEstimate = computed(
    () => this.browser && typeof navigator?.storage?.estimate === 'function',
  );

  /** Re-measure. Cheap enough to call on app start and after a bulk import. */
  async refresh(): Promise<StorageEstimateSnapshot> {
    if (!this.browser || typeof navigator?.storage?.estimate !== 'function') {
      // Unsupported: report unknown rather than guessing. Callers treat null as
      // "no information", never as "plenty of room".
      const unknown: StorageEstimateSnapshot = {
        usage: null,
        quota: null,
        ratio: null,
        persisted: false,
        measuredAt: Date.now(),
      };
      this.snapshot.set(unknown);
      return unknown;
    }

    try {
      const { usage = null, quota = null } = await navigator.storage.estimate();
      const persisted =
        typeof navigator.storage.persisted === 'function'
          ? await navigator.storage.persisted()
          : false;

      const next: StorageEstimateSnapshot = {
        usage: usage ?? null,
        quota: quota ?? null,
        ratio: usage != null && quota ? usage / quota : null,
        persisted,
        measuredAt: Date.now(),
      };
      this.snapshot.set(next);
      return next;
    } catch {
      // estimate() can reject under storage pressure - exactly when we most
      // want a number. Keep the last known value rather than clearing it.
      return this.snapshot();
    }
  }

  /**
   * Ask the browser to exempt this origin from eviction.
   *
   * Without this, Chrome may clear the database under disk pressure - taking
   * unsynced work with it. Chrome usually grants it silently for an installed
   * PWA or a site with real engagement, and the request is harmless when it
   * refuses, so it is worth asking once at startup.
   */
  async requestPersistence(): Promise<boolean> {
    if (!this.browser || typeof navigator?.storage?.persist !== 'function') {
      return false;
    }
    try {
      if (await navigator.storage.persisted()) return true;
      const granted = await navigator.storage.persist();
      await this.refresh();
      return granted;
    } catch {
      return false;
    }
  }

  /**
   * Gate for optional growth - prefetching, caching extra screens.
   *
   * Deliberately not applied to user writes: a teacher's answer must be saved
   * even on a nearly full disk. If that write is the one that finally exceeds
   * the quota, the repository surfaces StorageQuotaExceededError and the user
   * is told, which is far better than silently declining to record their work.
   */
  async canGrow(): Promise<boolean> {
    await this.refresh();
    return !this.isCritical();
  }

  /** Throw the typed error if the store is too full for optional growth. */
  async assertRoomToGrow(): Promise<void> {
    if (await this.canGrow()) return;
    const { usage, quota } = this.snapshot();
    throw new StorageQuotaExceededError(
      'Local storage is nearly full. Sync pending changes or clear cached data before downloading more.',
      usage,
      quota,
    );
  }

  /** Human-readable size for the UI. */
  static formatBytes(bytes: number | null): string {
    if (bytes === null) return 'unknown';
    const units = ['B', 'KB', 'MB', 'GB'];
    let value = bytes;
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) {
      value /= 1024;
      unit++;
    }
    return `${value.toFixed(value >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`;
  }
}
